import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import FormItem from '../../../components/Form/FormItem';
import Label from '../../../components/Form/Label';
import Input from '../../../components/Form/Input';
import Title from '../../../components/Title';
import { UserData } from '../../../types/userData';
import inputMask from '../../../util/mask';
import { FormInput, InfoDocument } from './styles';
import { ExclamationIcon } from '../PhoneForm/styles';
import { User } from '../../../types/user';
import { useAuth } from '../../../hooks/useAuth';
import useMedia from '../../../hooks/useMedia';
import { LoadingSpinner } from '../../../components/Loading/styles';
import { isValidCNPJ, validateCPFCNPJ } from '../../../util/validation';
import { api } from '../../../services/api';
import {
  Actions,
  Categories,
  GetInterventionCategory,
  Labels,
} from '../../../types/gtm';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Status from '../../../components/Status';
import { ModalBody, ModalButtons } from '../components/modals/styles';
import svg from '../../../static/img/account-management.svg';
import environmentConfig from '../../../config/environment-configs';

export interface Props {
  title?: string;
  isIntervention?: boolean;
  isInterventionFlow: boolean;
  width?: string;
  onConfirm?: () => void;
  onConfirmResult?: () => void;
  onInconsistentResult?: () => void;
  user?: User;
}

export type ModalState = {
  show: boolean;
  alreadyRequest?: boolean;
  globoId?: string;
  docNumber?: string;
  error?: string;
};

export type ModalProps = {
  modal: ModalState;
  setModal: (m: ModalState) => void;
  close: (b?: boolean) => void;
  onConfirm?: () => void;
  user?: User;
};

const DocumentForm = ({
  title,
  isIntervention,
  isInterventionFlow,
  width,
  user,
  onConfirm,
  onConfirmResult,
  onInconsistentResult,
}: Props) => {
  const { values, handleChange, errors, setFieldValue } =
    useFormikContext<UserData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [screenSuccess, setScreenSuccess] = useState<string>('');
  const { hideCPFIntervention, getValidToken } = useAuth();
  const history = useHistory();
  const mobile = useMedia('(max-width: 650px)');
  const [docNumberFocused, setDocNumberFocused] = useState<boolean>(false);

  const getRecaptchaToken = async (): Promise<string> => {
    return new Promise(resolve => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(environmentConfig.GOOGLE_RECAPTCHA_KEY, {
            action: 'emailMinhaConta',
          })
          .then((token: string) => {
            resolve(token);
          });
      });
    });
  };

  const generateErrorMessage = (
    userData: UserData,
    field: string,
    error?: string,
  ) => {
    if (error) {
      return error;
    }
    if (field === 'docNumber' && userData.hasInconsistentData) {
      return docNumberFocused
        ? ''
        : 'Você deve informar um CPF ou CNPJ válido.';
    }
    return '';
  };

  const secondText = values.isDocNumberLocked
    ? 'Você não pode editar essa informação'
    : '';

  const getDocumentButtonText = () => {
    return 'Confirmar CPF';
  };

  function convertDateFormat(dateString: string): string {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }

  function confirmDocNumberDisabled(): boolean {
    if (values.isDocNumberLocked || errors.birthDate) {
      return true;
    }
    if (
      values.docNumber &&
      validateCPFCNPJ(values.docNumber) &&
      !isValidCNPJ(values.docNumber)
    ) {
      return false;
    }
    return true;
  }

  const userToUpdate = {
    docNumber: inputMask.unMask(values.docNumber),
    fullName: values.fullName,
    birthDate: convertDateFormat(values.birthDate),
  };

  const GetAction = () => {
    if (getDocumentButtonText() === 'Confirmar CPF') {
      return Actions.ClickRegister;
    }
    return Actions.ClickChange;
  };

  const confirmDocument = async () => {
    if (loading) {
      return;
    }
    setLoading(true);

    const countChar = values.docNumber.length;

    const isValid = validateCPFCNPJ(values.docNumber);

    if (countChar !== 14) {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.CPFIntervention,
        event_action: GetAction(),
        event_label: Labels.InvalidFormat,
        bd_suser_code: user?.globoId,
      });
      setLoading(false);
    }
    if (isValid) {
      try {
        const token = await getValidToken();
        const recaptchaToken = await getRecaptchaToken();
        api
          .put('/user/validate/external', userToUpdate, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Recaptcha-Token': recaptchaToken,
            },
          })
          .then(() => {
            window.dataLayer.push({
              event: 'customEventUserInteraction',
              event_category: isInterventionFlow
                ? Categories.CPFIntervention
                : Categories.EditUserPersonalData,
              event_action: Actions.Success,
              event_label: Labels.RegisteredCPF,
              bd_suser_code: user?.globoId,
            });
            hideCPFIntervention(true);
            setLoading(false);
            setScreenSuccess('success');
            setFieldValue('isDocNumberLocked', true);
            setFieldValue('hasInconsistentData', false);
            if (onConfirmResult) {
              onConfirmResult();
            }
          })
          .catch((errorRequest: AxiosError) => {
            switch (errorRequest.response?.status) {
              case 429:
                window.dataLayer.push({
                  event: 'customEventUserInteraction',
                  event_category: Categories.CPFIntervention,
                  event_action: Actions.Error,
                  event_label: Labels.AttemptLimitReached,
                  bd_suser_code: user?.globoId,
                });
                setScreenSuccess('error');
                toast.error(
                  'Limite de tentativas atingido. Tente novamente mais tarde.',
                );
                break;
              case 400:
                window.dataLayer.push({
                  event: 'customEventUserInteraction',
                  event_category: Categories.CPFIntervention,
                  event_action: Actions.Error,
                  event_label: Labels.InconsistentData,
                  bd_suser_code: user?.globoId,
                });
                setDocNumberFocused(false);
                if (onInconsistentResult) {
                  onInconsistentResult();
                }
                setScreenSuccess('error');
                break;
              default:
                window.dataLayer.push({
                  event: 'customEventUserInteraction',
                  event_category: Categories.CPFIntervention,
                  event_action: Actions.Error,
                  event_label: Labels.ErrorOnScreenValidationCPF,
                  bd_suser_code: user?.globoId,
                });
                toast.error('Ocorreu um erro inesperado. Tente novamente.');
                break;
            }
          });
      } catch (error) {
        setLoading(false);
        toast.error('Ocorreu um erro inesperado. Tente novamente.');
      } finally {
        setLoading(false);
      }
    }
    if (!isValid) {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.CPFIntervention,
        event_action: GetAction(),
        event_label: Labels.NonexistentCPF,
        bd_suser_code: user?.globoId,
      });
      setLoading(false);
    }
  };

  return (
    <div>
      {screenSuccess === 'success' && (
        <Modal maxWidth="650px" contentWidth={mobile ? '100%' : '480px'}>
          <ModalBody>
            <Status
              icon={`${svg}#icon-check-confirm`}
              title=" Seu CPF foi cadastrado com sucesso, obrigado!"
              colorTitle="#000000"
            />
          </ModalBody>
          <Button
            variant="secondary"
            width="100%"
            onClick={() => {
              window.dataLayer.push({
                event: 'customEventUserInteraction',
                event_category: GetInterventionCategory(user),
                event_action: Actions.ClickBack,
                event_label: Labels.ScreenConfirmDocument,
                bd_suser_code: user?.globoId,
              });
              setScreenSuccess('');
              if (onConfirm) {
                onConfirm();
              }
            }}
          >
            Voltar
          </Button>
        </Modal>
      )}

      {screenSuccess === 'error' && (
        <Modal maxWidth="650px" contentWidth={mobile ? '100%' : '480px'}>
          <ModalBody>
            <Status
              icon={`${svg}#icon-external-validation-error`}
              title="Ops! Identificamos uma inconsistência nos seus dados."
              colorTitle="#000000"
            />
          </ModalBody>
          <ModalButtons>
            <Button
              variant="primary"
              width="100%"
              onClick={() => {
                window.dataLayer.push({
                  event: 'customEventUserInteraction',
                  event_category: GetInterventionCategory(user),
                  event_action: Actions.ClickBack,
                  event_label: Labels.ScreenConfirmDocument,
                  bd_suser_code: user?.globoId,
                });
                setScreenSuccess('');
                history.push('/alterar-dados', {
                  externalValidationError: true,
                });
              }}
            >
              Continuar
            </Button>
            <Button
              variant="secondary"
              width="100%"
              onClick={() => {
                window.dataLayer.push({
                  event: 'customEventUserInteraction',
                  event_category: GetInterventionCategory(user),
                  event_action: Actions.ClickSkip,
                  event_label: Labels.ErrorOnScreenValidationCPF,
                  bd_suser_code: user?.globoId,
                });
                setScreenSuccess('');
                if (onConfirm) {
                  onConfirm();
                }
              }}
            >
              Prefiro deixar para depois
            </Button>
          </ModalButtons>
        </Modal>
      )}

      {!isIntervention && (
        <FormItem>
          {title && <Title>{title}</Title>}
          <FormInput>
            <Label htmlFor="docNumber">CPF ou CNPJ</Label>
            <Input
              id="docNumber"
              value={
                values.isDocNumberLocked && values.docNumber
                  ? inputMask.hideDocument(values.docNumber)
                  : inputMask.cpf_cnpj(values.docNumber)
              }
              readOnly={values.isDocNumberLocked}
              onChange={handleChange}
              onClickCapture={() => setDocNumberFocused(true)}
              name="docNumber"
              placeHolderText="Informe seu CPF ou CNPJ"
              aria-label={`${secondText}`}
              errorMessage={generateErrorMessage(
                values,
                'docNumber',
                errors.docNumber,
              )}
              width={width}
              withButton
              buttonVariant="primary"
              buttonText={
                loading ? <LoadingSpinner isButton /> : getDocumentButtonText()
              }
              onClick={() => confirmDocument()}
              buttonDisabled={confirmDocNumberDisabled()}
            />
          </FormInput>
          <InfoDocument>
            <ExclamationIcon>!</ExclamationIcon>
            <p>
              Seu CPF nos ajuda a saber quem é você e torna sua experiência mais
              agradável.
            </p>
          </InfoDocument>
        </FormItem>
      )}

      {isIntervention && screenSuccess === '' && (
        <FormItem>
          {title && <Title>{title}</Title>}

          <FormInput>
            <Label htmlFor="docNumber">CPF</Label>
            <Input
              id="docNumber"
              value={inputMask.cpf(values.docNumber)}
              readOnly={values.isDocNumberLocked}
              onChange={handleChange}
              name="docNumber"
              placeHolderText="Digite seu CPF"
              aria-label={`${secondText}`}
              errorMessage={errors.docNumber ? errors.docNumber : ''}
              width={width}
              withButtonAlternative
              buttonVariant="secondary"
              buttonText={
                loading ? <LoadingSpinner isButton /> : getDocumentButtonText()
              }
              onClick={() => confirmDocument()}
              buttonDisabled={confirmDocNumberDisabled()}
            />
          </FormInput>
        </FormItem>
      )}
    </div>
  );
};

export default DocumentForm;
